/**
 * BeatLab helpers — client-side onset detection + spectral classification.
 * Given a recording of someone beatboxing, extract onset timestamps and
 * classify each hit as kick / snare / hihat by its spectral centroid.
 */

export async function analyseBeatbox(blob) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const ctx = new AC();
  const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
  ctx.close?.();

  const sr = buf.sampleRate;
  const data = buf.getChannelData(0);
  const duration = buf.length / sr;

  // 1) Onset detection via short-time energy first derivative
  const frame = Math.floor(sr * 0.020);
  const hop = Math.floor(sr * 0.005);
  const n = Math.floor((data.length - frame) / hop);
  const env = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let s = 0;
    for (let j = 0; j < frame; j++) s += Math.abs(data[i * hop + j]);
    env[i] = s / frame;
  }
  // Adaptive threshold (mean + 1.5*std of the derivative)
  const deriv = new Float32Array(n);
  for (let i = 1; i < n; i++) deriv[i] = Math.max(0, env[i] - env[i - 1]);
  let mean = 0;
  for (const v of deriv) mean += v; mean /= n;
  let std = 0;
  for (const v of deriv) std += (v - mean) ** 2; std = Math.sqrt(std / n);
  const thresh = mean + 1.5 * std;
  // Minimum gap between hits: 80ms (guard-band against MediaRecorder noise)
  const minGapFrames = Math.floor(0.080 / 0.005);
  const onsets = [];
  let lastHit = -Infinity;
  for (let i = 1; i < n - 1; i++) {
    if (deriv[i] > thresh && deriv[i] > deriv[i - 1] && deriv[i] >= deriv[i + 1] && (i - lastHit) > minGapFrames) {
      onsets.push(i * hop);
      lastHit = i;
    }
  }

  // 2) Classify each hit's timbre using FFT spectral centroid
  const FFT_SIZE = 1024;
  const hits = onsets.map(startSample => {
    const chunk = data.slice(startSample, startSample + FFT_SIZE);
    if (chunk.length < FFT_SIZE) return null;
    // Naive DFT — fine because we only run this on short clips at analysis time
    const magnitudes = new Float32Array(FFT_SIZE / 2);
    for (let k = 0; k < FFT_SIZE / 2; k++) {
      let re = 0, im = 0;
      for (let t = 0; t < FFT_SIZE; t++) {
        const ang = -2 * Math.PI * k * t / FFT_SIZE;
        re += chunk[t] * Math.cos(ang);
        im += chunk[t] * Math.sin(ang);
      }
      magnitudes[k] = Math.sqrt(re * re + im * im);
    }
    let num = 0, den = 0;
    for (let k = 0; k < magnitudes.length; k++) {
      const f = k * sr / FFT_SIZE;
      num += f * magnitudes[k];
      den += magnitudes[k];
    }
    const centroid = den > 0 ? num / den : 0;
    // Empirical thresholds for beatbox timbre
    let cls;
    if (centroid < 900) cls = 'kick';
    else if (centroid < 3500) cls = 'snare';
    else cls = 'hihat';
    return { at_sec: Math.round((startSample / sr) * 1000) / 1000, class: cls, centroid: Math.round(centroid) };
  }).filter(Boolean);

  // 3) Rough BPM estimate from onset intervals (median)
  let bpm = null;
  if (hits.length >= 4) {
    const intervals = [];
    for (let i = 1; i < hits.length; i++) intervals.push(hits[i].at_sec - hits[i - 1].at_sec);
    intervals.sort((a, b) => a - b);
    const median = intervals[Math.floor(intervals.length / 2)];
    if (median > 0) {
      let b = 60 / median;
      while (b > 180) b /= 2;
      while (b < 70) b *= 2;
      bpm = Math.round(b * 10) / 10;
    }
  }

  return { duration, hits, bpm, count: hits.length };
}

/**
 * Simple in-browser drum synth. No sample files needed — we generate the
 * kick/snare/hihat with the Web Audio API so the feature ships with zero assets.
 */
export function playDrumSchedule(hits, bpmScale = 1) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const ctx = new AC();
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.6;
  master.connect(ctx.destination);
  hits.forEach(h => triggerDrum(ctx, master, h.class, now + (h.at_sec / bpmScale)));
  // Close the context after playback finishes (adds 500ms of tail)
  const total = hits.length ? (hits[hits.length - 1].at_sec / bpmScale + 0.5) : 0.5;
  setTimeout(() => { try { ctx.close(); } catch {} }, total * 1000 + 200);
  return ctx;
}

function triggerDrum(ctx, out, kind, t) {
  if (kind === 'kick') {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.15);
    gain.gain.setValueAtTime(1, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    osc.connect(gain).connect(out);
    osc.start(t); osc.stop(t + 0.35);
  } else if (kind === 'snare') {
    // Body + noise
    const osc = ctx.createOscillator();
    const gainO = ctx.createGain();
    osc.frequency.setValueAtTime(220, t);
    gainO.gain.setValueAtTime(0.35, t);
    gainO.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    osc.connect(gainO).connect(out);
    osc.start(t); osc.stop(t + 0.12);

    const bufSize = ctx.sampleRate * 0.2;
    const noiseBuf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const noiseData = noiseBuf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) noiseData[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    const hpf = ctx.createBiquadFilter();
    hpf.type = 'highpass'; hpf.frequency.value = 1500;
    const gainN = ctx.createGain();
    gainN.gain.setValueAtTime(0.7, t);
    gainN.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    noise.connect(hpf).connect(gainN).connect(out);
    noise.start(t); noise.stop(t + 0.2);
  } else {
    // Hi-hat: short filtered noise
    const bufSize = ctx.sampleRate * 0.05;
    const noiseBuf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const noiseData = noiseBuf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) noiseData[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    const hpf = ctx.createBiquadFilter();
    hpf.type = 'highpass'; hpf.frequency.value = 7000;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.4, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
    noise.connect(hpf).connect(gain).connect(out);
    noise.start(t); noise.stop(t + 0.05);
  }
}

/**
 * Bake the classified hits into a WAV file we can upload/post as a real track.
 * Uses OfflineAudioContext so it runs faster than real time.
 */
export async function renderBeatToWav(hits, bpmScale = 1) {
  const OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  const sr = 44100;
  const total = hits.length ? (hits[hits.length - 1].at_sec / bpmScale + 0.6) : 0.6;
  const ctx = new OAC(2, Math.ceil(sr * total), sr);
  const master = ctx.createGain();
  master.gain.value = 0.6;
  master.connect(ctx.destination);
  hits.forEach(h => triggerDrum(ctx, master, h.class, h.at_sec / bpmScale));
  const rendered = await ctx.startRendering();
  return audioBufferToWav(rendered);
}

// Standard PCM WAV encoder for a rendered AudioBuffer
function audioBufferToWav(buffer) {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length * numChannels * 2 + 44;
  const arrayBuffer = new ArrayBuffer(length);
  const view = new DataView(arrayBuffer);
  const writeString = (offset, s) => { for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i)); };
  writeString(0, 'RIFF'); view.setUint32(4, length - 8, true); writeString(8, 'WAVE');
  writeString(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true); view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true); view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true); writeString(36, 'data'); view.setUint32(40, length - 44, true);
  let offset = 44;
  const channels = [];
  for (let c = 0; c < numChannels; c++) channels.push(buffer.getChannelData(c));
  for (let i = 0; i < buffer.length; i++) {
    for (let c = 0; c < numChannels; c++) {
      const sample = Math.max(-1, Math.min(1, channels[c][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
  }
  return new Blob([arrayBuffer], { type: 'audio/wav' });
}
